import { PeoplePicker, PersonType } from "@microsoft/mgt-react";
import { isEqual } from "lodash";
import { Dispatch, FC, Fragment, useCallback, useMemo } from "react";
import { approverType } from "../../../../../../constants/itCodeRequestConstants";
import { useSelector } from "../../../../../../store/store";
import InformationList, { ColumnContent, LeftRightContent, ListContent } from "../../../../components/InformationList";
import TableModal from "../../../../components/detail";
import { useProcess } from "../../../../process-context";
import { FileAttachmentCard } from "../../../../upload";
import CommentSection from "../../../commentsection/comment-sections";
import { tableColumns } from "./table-column";
import { ViagsUpload } from "../../../../../../components";
import { FileHelper } from "../../../../../../helper";
import { Space } from "antd";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { requestStatus } from "../../../../../../constants";

interface Props {
  setAssignList: Dispatch<any>;
  setITHeadSelected: Dispatch<any>;
  setITHeadName: Dispatch<any>;
  setFileAttachment: Dispatch<any>;
}

const FormContent: FC<Props> = (props) => {
  const { setAssignList, setITHeadSelected, setITHeadName, setFileAttachment } = props;

  const { Request, approvalStep, approvalType, comment, setComment } = useProcess();

  const { fields } = Request;

  /**
   * Selector
   */
  const user = useSelector((state) => state.user);
  const softwares = useSelector((state: any) => state.systemMgmt.softwares);

  const {
    eSoftwareId,
    eDescription,
    eDeptHeadComment,
    eITLeadComment,
    eITHeadComment,
    eAssigneeComment,
    eApprovedBy,
    eApprovedByITLead,
    eApprovedByITHead,
    eAssignee,
    eRequestStatus,
  } = fields;

  const onEmployeeChange = useCallback(
    (selectedOptions: any) => {
      const assignedList = selectedOptions?.target?.selectedPeople;
      setAssignList(assignedList);
    },
    [setAssignList],
  );

  const onITHeadChange = useCallback(
    (selectedOptions: any) => {
      setITHeadSelected(selectedOptions && selectedOptions?.target?.selectedPeople[0]?.userPrincipalName);
      setITHeadName(selectedOptions ? selectedOptions?.target?.selectedPeople[0]?.displayName : null);
    },
    [setITHeadName, setITHeadSelected],
  );

  const handleUpload = useCallback(
    async (info: any) => {
      if (info?.fileList?.length > 0) {
        const mappedFile = [
          ...info.fileList.map(async (e: any) => {
            const base64 = await FileHelper.getBase64(e.originFileObj);
            const stringBase64 = base64.split(",")[1];
            return { ...e, base64: stringBase64 };
          }),
        ];
        const res = await Promise.all(mappedFile);
        setFileAttachment(res);
      } else {
        const { ...file } = info.file;
        const base64 = await FileHelper.getBase64(info?.file.originFileObj);
        file.base64 = base64.split(",")[1];
        setFileAttachment([file]);
      }
    },
    [setFileAttachment],
  );

  const comments = useMemo(() => {
    return [
      {
        hidden: !eDeptHeadComment,
        content: `Comment của ${eApprovedBy.split("|")[1]}: ${eDeptHeadComment}`,
      },
      {
        hidden: !eITLeadComment,
        content: `Comment của ${eApprovedByITLead.split("|")[1]}: ${eITLeadComment}`,
      },
      {
        hidden: !eITHeadComment,
        content: `Comment của ${eApprovedByITHead.split("|")[1]}: ${eITHeadComment}`,
      },
      {
        hidden: !eAssigneeComment,
        content: `Comment của ${eAssignee.split("|")[1]}: ${eAssigneeComment}`,
      },
      {
        hidden: approvalStep !== approvalType,
        content: (
          <div className="input">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
        ),
      },
    ];
  }, [
    eDeptHeadComment,
    eApprovedBy,
    eITLeadComment,
    eApprovedByITLead,
    eITHeadComment,
    eApprovedByITHead,
    eAssigneeComment,
    eAssignee,
    approvalStep,
    approvalType,
    comment,
    setComment,
  ]);

  const smColData = useMemo(() => {
    return [
      {
        content: (
          <LeftRightContent left="Người yêu cầu:" right={<strong>{Request.createdBy.user.displayName}</strong>} />
        ),
      },
      {
        content: (
          <LeftRightContent
            left="SĐT Người yêu cầu:"
            right={<strong>{Request.fields.ePhoneNumber}</strong>}
            smColumn={false}
          />
        ),
      },
      {
        content: <LeftRightContent left="Cơ quan/Đơn vị:" right={<strong>{Request.fields.eDepartment}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnit, Request.fields.eDepartment),
        },
        content: <LeftRightContent left="Đơn vị:" right={<strong>{Request.fields.eUnit}</strong>} />,
      },
      {
        hidden: {
          sm: isEqual(Request.fields.eUnit, Request.fields.eUnion),
        },
        content: <LeftRightContent left="Đội/Tổ:" right={<strong>{Request.fields.eUnion}</strong>} />,
      },
      {
        content: (
          <LeftRightContent
            left="Phần mềm:"
            right={softwares && eSoftwareId && softwares.find((v: any) => v.id === eSoftwareId)?.Title}
          />
        ),
      },
      {
        content: <LeftRightContent left="Mô tả:" right={eDescription} />,
      },
    ];
  }, [Request, softwares, eSoftwareId, eDescription]);

  const fullColData = useMemo(() => {
    return [
      {
        content: <TableModal data={JSON.parse(Request.fields.eContent)} columns={tableColumns} />,
      },
      {
        content: (
          <Fragment>
            Tệp đính kèm
            <div className="mt-2 d-flex flex-column">
              <FileAttachmentCard
                listFile={Request.fields.eFileContent ? JSON.parse(Request.fields.eFileContent) : []}
              />
            </div>
          </Fragment>
        ),
      },
      {
        content: (
          <ListContent
            label="Comments"
            data={comments}
            getItemHidden={(item) => item.hidden}
            renderItemContent={(item) => item.content}
          />
        ),
      },
      {
        hidden: approvalType !== approverType.assignee || eRequestStatus === requestStatus.completed,
        content: (
          <ViagsUpload onChange={(info) => handleUpload(info)} type="select">
            <Space style={{ fontSize: 15 }}>
              HDSD đính kèm (nếu có)
              <AiOutlineCloudUpload size={24} />
              (word, excel, pdf, jpg,...)
            </Space>
          </ViagsUpload>
        ),
      },
      {
        hidden: eRequestStatus !== requestStatus.completed,
        content: (
          <Fragment>
            HDSD đính kèm
            <div className="mt-2 d-flex flex-column">
              <FileAttachmentCard
                listFile={Request.fields.eAssigneeAttachment ? JSON.parse(Request.fields.eAssigneeAttachment) : []}
              />
            </div>
          </Fragment>
        ),
      },
      {
        content: (
          <Fragment>
            Cập nhật thông tin
            <div className="mt-2">
              <CommentSection comments={[]} data={Request} user={user} id={2} className="mb-4" />
            </div>
          </Fragment>
        ),
      },
      {
        hidden:
          approvalType !== approverType.itlead ||
          approvalStep !== approvalType ||
          Request.fields.eDepartment === "Phòng Công nghệ thông tin",
        content: (
          <ColumnContent
            label="Đơn vị quản lý chương trình"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn đơn vị quản lý chương trình"
                  type={PersonType.any}
                  selectionChanged={onITHeadChange}
                />
              </div>
            }
          />
        ),
      },
      {
        hidden: approvalType !== approverType.itlead || approvalStep !== approvalType,
        content: (
          <ColumnContent
            label="Cán bộ hoàn tất nghiệm thu"
            content={
              <div className="people_picker-custom-detail">
                <PeoplePicker
                  className="people-picker"
                  placeholder="Vui lòng chọn cán bộ hoàn tất nghiệm thu"
                  type={PersonType.any}
                  selectionChanged={onEmployeeChange}
                />
              </div>
            }
          />
        ),
      },
    ];
  }, [
    Request,
    comments,
    approvalType,
    eRequestStatus,
    user,
    approvalStep,
    onITHeadChange,
    onEmployeeChange,
    handleUpload,
  ]);

  return (
    <Fragment>
      <InformationList
        size="sm-col"
        data={smColData}
        renderContent={(item) => item.content}
        getItemHidden={(item) => item.hidden}
      />
      <InformationList
        size="full-col"
        data={fullColData}
        getItemHidden={(item) => item.hidden}
        renderContent={(item) => item.content}
      />
    </Fragment>
  );
};

export default FormContent;
